/**
 * AddAccess Controller - Exact replica of v1 AddAccess/RemoveAccess logic
 * This controller handles ONLY AddAccess and RemoveAccess flows
 */

import type { Dispatch } from '@reduxjs/toolkit';

import { type RootState } from '../../../services/serviceTypes';
import {
  addMessage,
  addMessageToEntitlement,
  incrementEntitlementIndex,
  removeMessagesBySubType,
  setAddAccessPrompt,
  updateStepFormData,
} from '../data-store/copilotSlice';

import { buildCumulativePayload } from './mappingReplacementUtils';
import { isEditModeFromUrl } from './payloadProcessingUtils';

interface AddAccessControllerConfig {
  dispatch: Dispatch;
  getState: () => unknown;
  services: {
    saveUseCaseConfiguration: (payload: unknown) => Promise<unknown>;
  };
}

export default class AddAccessController {
  private dispatch: Dispatch;

  private getState: () => unknown;

  private services: AddAccessControllerConfig['services'];

  // Instance variables like v1
  private addAccessAttributePayload: Record<string, unknown> | null = null;

  constructor(config: AddAccessControllerConfig) {
    this.dispatch = config.dispatch;
    this.getState = config.getState;
    this.services = config.services;
  }

  showAddAccessConfirmation(payload: Record<string, unknown>) {
    this.dispatch(
      updateStepFormData({
        stepName: 'AddAccessConfirmation',
        formData: payload,
      })
    );

    this.addAccessAttributePayload = payload;

    // Get the current entitlement index from the state
    const state = this.getState() as RootState;

    const { activeStep } = state.copilot.navigation;
    let entitlementIndex = 0;

    // Extract entitlement index from activeStep (e.g., 'AddAccess_0' -> 0)
    if (activeStep && activeStep.includes('AddAccess_')) {
      entitlementIndex = parseInt(activeStep.split('_')[1], 10) || 0;
    }

    const threeButtonMessage = {
      id: `addAccess-confirmation-${Date.now()}`,
      type: 'ai_response' as const,
      subType: 'threeButtonsHelperText' as const,
      timestamp: Date.now(),
      buttons: [
        { label: 'Specify Prompt', action: 'addaccess_prompt_yes' },
        { label: 'Upload Browser Recording', action: 'addaccess_upload_yes' },
        { label: 'Skip for Now', action: 'handle_No_add_access' },
      ],
    };

    // Always use the parent-child approach for entitlement messages
    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex,
        entitlementType: 'addAccess',
        message: threeButtonMessage,
      })
    );
  }

  handleAccessAttributeMappingSubmit(
    formData: Record<string, unknown>,
    isValid: boolean
  ): void {
    if (!isValid) {
      return;
    }

    this.showAddAcessConfirmation(formData);
  }

  showAddAcessConfirmation(payload: Record<string, unknown>) {
    this.dispatch(
      updateStepFormData({
        stepName: 'AddAccessConfirmation',
        formData: payload,
      })
    );

    const state = this.getState() as RootState;

    const generalFormData = state.copilot.stepFormData.General || {};
    const systemNameField = generalFormData['securitysystems.systemname'] as
      | { value?: string }
      | string
      | undefined;
    const applicationName = String(
      (systemNameField as { value?: string }).value || systemNameField || ''
    );

    // Extract entitlement index from activeStep
    const activeStep = state.copilot.navigation.activeStep || '';
    let entitlementIndex = 0;

    if (activeStep.includes('AddAccess_')) {
      entitlementIndex = parseInt(activeStep.split('_')[1], 10) || 0;
    }

    const threeButtonMessage = {
      id: `addAccess-confirmation-${Date.now()}`,
      type: 'ai_response' as const,
      subType: 'threeButtonsHelperText' as const,
      timestamp: Date.now(),
      values: [applicationName],
      buttons: [
        { label: 'Specify Prompt', action: 'addaccess_prompt_yes' },
        { label: 'Upload Browser Recording', action: 'addaccess_upload_yes' },
        { label: 'Skip for Now', action: 'handle_No_add_access' },
      ],
    };

    // Always use the parent-child approach for entitlement messages
    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex,
        entitlementType: 'addAccess',
        message: threeButtonMessage,
      })
    );
  }

  async handleAddAccessNo(): Promise<void> {
    const state = this.getState() as RootState;

    // Get current entitlement index from activeStep
    const activeStep = state.copilot.navigation.activeStep || '';
    let entitlementIndex = 0;

    if (activeStep.includes('AddAccess_')) {
      entitlementIndex = parseInt(activeStep.split('_')[1], 10) || 0;
    } else {
      entitlementIndex = state.copilot.entitlementIteration.currentIndex || 0;
    }

    // Add user response to the current entitlement
    this.dispatch(
      addMessageToEntitlement({
        entitlementIndex,
        entitlementType: 'addAccess',
        message: {
          id: Date.now().toString(),
          type: 'user_response',
          subType: 'userResponseNo',
          text: 'Skip for Now',
          timestamp: Date.now(),
        },
      })
    );

    if (this.services.saveUseCaseConfiguration) {
      try {
        const isEditMode = isEditModeFromUrl();
        const payloadToProcess = this.buildPayloadFromRedux();

        await this.services.saveUseCaseConfiguration({
          applicationtype: 'CUA',
          endpointKey: this.getEndpointKey(),
          body: payloadToProcess,
        });

        // Check if we're editing an existing entitlement sequence
        const isEditingExistingSequence =
          isEditMode &&
          entitlementIndex <
            state.copilot.entitlementIteration.entitlements.length - 1 &&
          this.messagesContainEntitlementForm(entitlementIndex + 1);

        if (isEditingExistingSequence) {
          return;
        }

        this.handleNavigationAfterApiCall();
      } catch (error) {
        this.showFinalConfirmation?.();
      }
    }
  }

  handleAddAccessFile(file: File): void {
    this.dispatch(
      addMessage({
        id: `file-prompt-${Date.now()}`,
        type: 'file_prompt',
        subType: 'file_display',
        file,
        timestamp: Date.now(),
      })
    );

    const analyzingMessageId = `analyzing-${Date.now()}`;

    this.dispatch(
      addMessage({
        id: analyzingMessageId,
        type: 'ai_response',
        subType: 'analyzing',
        timestamp: Date.now(),
        loader: true,
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: false });

    setTimeout(() => {
      this.dispatch(removeMessagesBySubType('analyzing'));

      const extractedInstructions = `Instructions extracted from ${file.name}`;

      this.handleAddAccessPromptWithIndex(extractedInstructions).catch(() => {
        // Handle errors silently
      });
    }, 2000);
  }

  handleUserPrompt(userPrompt: string): void {
    this.handleAddAccessPromptWithIndex(userPrompt).catch(() => {
      // Handle errors silently
    });
  }

  async handleAddAccessPromptWithIndex(userPrompt: string): Promise<void> {
    this.dispatch(removeMessagesBySubType('movingToNextStep'));

    const state = this.getState() as RootState;
    const isEditMode = isEditModeFromUrl();

    if (isEditMode) {
      const successMessageExists = state.copilot.messages.some(
        (msg) => msg.subType === 'addAccessUpdateSuccess'
      );

      if (successMessageExists) {
        this.dispatch(removeMessagesBySubType('addAccessUpdateSuccess'));
      }
    }

    if (this.services.saveUseCaseConfiguration) {
      try {
        const currentIndex =
          state.copilot.entitlementIteration.currentIndex ?? 0;

        this.dispatch(
          setAddAccessPrompt({
            index: currentIndex,
            prompt: userPrompt,
          })
        );

        const payloadToProcess = this.buildPayloadFromRedux(userPrompt);

        await this.services.saveUseCaseConfiguration({
          applicationtype: 'CUA',
          endpointKey: this.getEndpointKey(),
          body: payloadToProcess,
        });

        // Check if we're editing an existing entitlement sequence
        const isEditingExistingSequence =
          isEditMode &&
          state.copilot.entitlementIteration.currentIndex <
            state.copilot.entitlementIteration.entitlements.length - 1 &&
          this.messagesContainEntitlementForm(
            state.copilot.entitlementIteration.currentIndex + 1
          );

        if (isEditingExistingSequence) {
          return;
        }

        this.handleNavigationAfterApiCall();
      } catch (error) {
        this.showFinalConfirmation?.();
      }
    }
  }

  // Handle AddAccess prompt button
  handleAddAccessPromptYes(): void {
    const state = this.getState() as {
      copilot?: {
        entitlementIteration?: {
          currentIndex: number;
        };
      };
    };

    const currentIndex = state.copilot?.entitlementIteration?.currentIndex ?? 0;

    this.dispatch(
      addMessage({
        id: Date.now().toString(),
        type: 'user_response',
        subType: 'userResponseYes',
        text: 'Specify Prompt',
        timestamp: Date.now(),
        parentEntitlementIndex: currentIndex,
        entitlementType: 'addAccess',
      })
    );

    this.dispatch(
      addMessage({
        id: `provide-additional-info-${Date.now()}`,
        type: 'ai_response',
        subType: 'provideAdditionalInfo',
        timestamp: Date.now(),
        parentEntitlementIndex: currentIndex,
        entitlementType: 'addAccess',
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: true });
    this.dispatch({ type: 'copilot/setPromptFooterMode', payload: 'text' });
  }

  // Handle AddAccess upload button
  handleAddAccessUploadYes(): void {
    const state = this.getState() as {
      copilot?: {
        entitlementIteration?: {
          currentIndex: number;
        };
      };
    };

    const currentIndex = state.copilot?.entitlementIteration?.currentIndex ?? 0;

    this.dispatch(
      addMessage({
        id: Date.now().toString(),
        type: 'user_response',
        subType: 'userResponseYes',
        text: 'Upload Browser Recording',
        timestamp: Date.now(),
        parentEntitlementIndex: currentIndex,
        entitlementType: 'addAccess',
      })
    );

    this.dispatch(
      addMessage({
        id: `upload-file-request-${Date.now()}`,
        type: 'ai_response',
        subType: 'provideAdditionalInfo',
        timestamp: Date.now(),
        parentEntitlementIndex: currentIndex,
        entitlementType: 'addAccess',
      })
    );

    this.dispatch({ type: 'copilot/setPromptFooterEnabled', payload: true });
    this.dispatch({ type: 'copilot/setPromptFooterMode', payload: 'upload' });
  }

  private messagesContainEntitlementForm(entitlementIndex: number): boolean {
    const state = this.getState() as {
      copilot?: {
        messages?: {
          type?: string;
          addAccessEntitlementIndex?: number;
        }[];
      };
    };

    return (
      state.copilot?.messages?.some(
        (msg) =>
          msg.type === 'step_form' &&
          msg.addAccessEntitlementIndex === entitlementIndex
      ) ?? false
    );
  }

  private messagesContainRemoveAccessForm(): boolean {
    const state = this.getState() as {
      copilot?: {
        messages?: {
          type?: string;
          subType?: string;
        }[];
      };
    };

    return (
      state.copilot?.messages?.some(
        (msg) => msg.type === 'step_form' && msg.subType === 'removeAccess'
      ) ?? false
    );
  }

  showNextEntitlementForm?: (index: number, entitlementName: string) => void;

  moveToRemoveAccess?: () => void;

  showFinalConfirmation?: () => void;

  /**
   * Centralized payload building from Redux state
   */
  private buildPayloadFromRedux(userPrompt?: string): Record<string, unknown> {
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.entitlementIteration.currentIndex ?? 0;
    const entitlements = state.copilot.entitlementIteration.entitlements ?? [];

    // Get all prompts, including the new one if provided
    const allPrompts = state.copilot.addAccessPrompts || {};
    const updatedPrompts = userPrompt
      ? { ...allPrompts, [currentIndex]: userPrompt }
      : allPrompts;

    const { parsedTemplates } = state.copilot;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-member-access
    const originalTemplate =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-member-access
      (parsedTemplates.AddAccess?.attributeDefaultValue as string) ?? '';

    if (originalTemplate) {
      return buildCumulativePayload(
        originalTemplate,
        entitlements,
        state.copilot.stepFormData || {},
        currentIndex,
        updatedPrompts
      );
    }

    // Fallback to existing payload if template not available
    return state.copilot.stepFormData.AddAccessConfirmation || {};
  }

  /**
   * Centralized endpoint key retrieval
   */
  private getEndpointKey(): number | null {
    const state = this.getState() as RootState;
    let { endpointKey } = state.copilot;

    if (!endpointKey) {
      const urlParams = new URLSearchParams(window.location.search);
      const endpointKeyFromUrl = urlParams.get('endpointKey');

      if (endpointKeyFromUrl) {
        endpointKey = parseInt(endpointKeyFromUrl, 10);
      }
    }

    return endpointKey;
  }

  /**
   * Centralized navigation logic after API calls
   */
  private handleNavigationAfterApiCall(): void {
    const state = this.getState() as RootState;
    const currentIndex = state.copilot.entitlementIteration.currentIndex ?? 0;
    const entitlements = state.copilot.entitlementIteration.entitlements ?? [];

    if (entitlements.length > 0 && currentIndex < entitlements.length - 1) {
      const nextIndex = currentIndex + 1;

      if (this.messagesContainEntitlementForm(nextIndex)) {
        return;
      }

      this.dispatch(incrementEntitlementIndex());

      this.dispatch(
        addMessage({
          id: `moving-to-add-access-${Date.now()}`,
          type: 'ai_response',
          subType: 'attributeMappingNext',
          timestamp: Date.now(),
          values: [entitlements[nextIndex].value],
          loader: true,
        })
      );

      this.showNextEntitlementForm?.(nextIndex, entitlements[nextIndex].value);
    } else {
      if (this.messagesContainRemoveAccessForm()) {
        return;
      }

      this.moveToRemoveAccess?.();
    }
  }
}
